import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCompareHorizontal, mdiLoading, mdiWrench } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { formatDisplayNumber } from '../../lib/format'

/** Payload carried by a `data_consistency` issue whose
 * `properties.check === "property_reconciliation"` (jobsrun/reconciliation.go
 * `converge`) — the declared value (with provenance) vs the observed
 * equipment count, plus enough context to PATCH or deep-link. */
export type ReconciliationProps = {
    check?: string
    /** Code of the property_aggregations entry the declared property is
     * reconciled against (properties.reconciliation.aggregation_code). */
    aggregation_code?: string
    declared_property_code?: string
    declared_property_name?: Record<string, string>
    child_asset_type_code?: string
    declared_value?: number
    observed_value?: number
    declared_source_details?: { source?: string; type?: string } | null
    declared_recorded_at?: string
    asset_uid?: string
}

type Props = {
    orgCode: string
    issueUid: string
    properties: ReconciliationProps
    /** Called after a successful "Adopt observed" so the caller can refresh
     * the issue (it just closed). */
    onAdopted?: () => void
}

function formatNumber(v: number | undefined): string {
    if (v == null) return '—'
    return formatDisplayNumber(v)
}

/** The comparison card of a declared-vs-observed reconciliation issue (spec
 * 2026-07-24-04-declared-vs-observed-property-reconciliation.md): states the
 * declared value (with its provenance) next to the observed equipment count,
 * and offers the two resolution paths — adopt the observed value, or go
 * complete the inventory so the next aggregation pass closes the issue on
 * its own. Equipment assets stay the source of truth: this card only ever
 * copies observed -> declared, never the reverse, and never creates assets. */
export function ReconciliationCard({ orgCode, issueUid, properties, onAdopted }: Props) {
    const { t, i18n } = useTranslation('issues')
    const [justAdopted, setJustAdopted] = useState(false)

    const declaredLabel = properties.declared_property_name
        ? properties.declared_property_name[i18n.language] ||
          properties.declared_property_name.en ||
          properties.declared_property_name.fr ||
          Object.values(properties.declared_property_name)[0]
        : properties.declared_property_code

    const declaredProvenance = properties.declared_recorded_at
        ? t('reconciliation.declared_provenance', {
              source:
                  properties.declared_source_details?.source ||
                  properties.declared_source_details?.type ||
                  t('detail.system_creator'),
              date: new Date(properties.declared_recorded_at).toLocaleDateString(i18n.language),
          })
        : undefined

    const adoptObserved = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/issues/{issueUid}/reconciliation/adopt-observed',
        {
            onSuccess: () => {
                setJustAdopted(true)
                onAdopted?.()
            },
        },
    )

    const assetUid = properties.asset_uid
    const equipmentTabHref =
        assetUid && properties.child_asset_type_code
            ? `/${orgCode}/assets/${assetUid}?tab=equipment&addEquipmentType=${properties.child_asset_type_code}`
            : undefined

    return (
        <Card data-testid="reconciliation-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiCompareHorizontal} size="sm" />
                    {t('reconciliation.card_title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {declaredLabel && (
                    <p className="text-sm text-muted-foreground" data-testid="reconciliation-property-label">
                        {declaredLabel}
                    </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                            {t('reconciliation.declared_label')}
                        </label>
                        <p className="text-lg font-semibold text-foreground" data-testid="reconciliation-declared-value">
                            {formatNumber(properties.declared_value)}
                        </p>
                        {declaredProvenance && (
                            <p className="text-xs text-muted-foreground">{declaredProvenance}</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">
                            {t('reconciliation.observed_label')}
                        </label>
                        <p className="text-lg font-semibold text-foreground" data-testid="reconciliation-observed-value">
                            {formatNumber(properties.observed_value)}
                        </p>
                        {properties.child_asset_type_code && (
                            <p className="text-xs text-muted-foreground">
                                {t('reconciliation.observed_provenance', {
                                    count: properties.observed_value ?? 0,
                                    assetType: properties.child_asset_type_code,
                                })}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                    <Button
                        size="sm"
                        disabled={adoptObserved.isPending || justAdopted}
                        onClick={() => adoptObserved.mutate({ params: { path: { orgCode, issueUid } } })}
                        title={t('reconciliation.adopt_observed_hint', { value: formatNumber(properties.observed_value) })}
                        data-testid="reconciliation-adopt-observed-btn"
                    >
                        {adoptObserved.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-1 motion-safe:animate-spin" /> : null}
                        {adoptObserved.isPending
                            ? t('reconciliation.adopt_observed_pending')
                            : t('reconciliation.adopt_observed')}
                    </Button>
                    {equipmentTabHref && (
                        <Button size="sm" variant="outline" asChild title={t('reconciliation.complete_inventory_hint')}>
                            <a href={equipmentTabHref} data-testid="reconciliation-complete-inventory-link">
                                <AppIcon path={mdiWrench} size="sm" className="mr-1" />
                                {t('reconciliation.complete_inventory')}
                            </a>
                        </Button>
                    )}
                </div>
                {adoptObserved.isError && (
                    <p className="text-xs text-destructive" data-testid="reconciliation-adopt-error">
                        {t('reconciliation.adopt_observed_error', { defaultValue: 'Could not adopt the observed value.' })}
                    </p>
                )}
            </CardContent>
        </Card>
    )
}
